import React, { useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  TableFooter,
  LinearProgress,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Stack,
} from "@mui/material";
import ActionButtons from "./ActionButtons";

const DataTable = ({
  columns,
  rows,
  page,
  totalPages,
  onPageChange,
  onAction,
  actions = ["view", "edit", "delete"],
  getRowId = (row) => row._id ?? row.id,
  loading = false,
  emptyMessage = "No data available",
  paginationAlign = "center",
  tableLabel = "Data Table",
  itemsPerPage,
  itemsPerPageOptions = [10, 25, 50, 100],
  onItemsPerPageChange,
  ...containerProps
}) => {
  const renderCell = useCallback((col, row, rowIdx) => {
    const val = row[col.id];
    return col.render ? col.render(val, row, rowIdx) : val;
  }, []);

  const hasActions = actions.length > 0;
  const colSpan = columns.length + (hasActions ? 1 : 0);

  return (
    <Box>
      <TableContainer
        component={Paper}
        elevation={0}
        variant="outlined"
        aria-label={tableLabel}
        {...containerProps}
      >
        {loading && <LinearProgress />}

        <Table size="small" aria-label={tableLabel}>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c.id}
                  align={c.align || "left"}
                  sx={{ fontWeight: "bold", py: 2 }}
                >
                  {c.label}
                </TableCell>
              ))}
              {hasActions && (
                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => {
                const id = getRowId(row);
                return (
                  <TableRow hover key={`row-${id}`}>
                    {columns.map((c) => (
                      <TableCell
                        sx={{ py: 2 }}
                        key={`${c.id}-${id}`}
                        align={c.align || "left"}
                      >
                        {renderCell(c, row, idx)}
                      </TableCell>
                    ))}
                    {hasActions && (
                      <TableCell>
                        <ActionButtons
                          id={id}
                          actions={actions}
                          onAction={onAction}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>

          {(totalPages > 1 || onItemsPerPageChange) && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={colSpan} sx={{ py: 2 }}>
                  <Stack
                    spacing={2}
                    direction={"row"}
                    justifyContent={paginationAlign}
                  >
                    {/* Items Per Page Selector */}
                    {onItemsPerPageChange && (
                      <FormControl variant="standard" size="small">
                        <Select
                          value={itemsPerPage}
                          onChange={(e) =>
                            onItemsPerPageChange(Number(e.target.value))
                          }
                        >
                          {itemsPerPageOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {totalPages > 1 && (
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, p) => onPageChange(p)}
                        color="primary"
                        showFirstButton
                        showLastButton
                      />
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      align: PropTypes.oneOf(["left", "center", "right"]),
      render: PropTypes.func,
    })
  ).isRequired,
  rows: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onAction: PropTypes.func,
  actions: PropTypes.arrayOf(
    PropTypes.oneOf(["view", "edit", "delete", "custom"])
  ),
  getRowId: PropTypes.func,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.node,
  paginationAlign: PropTypes.oneOf(["left", "center", "right"]),
  tableLabel: PropTypes.string,
  itemsPerPage: PropTypes.number,
  itemsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  onItemsPerPageChange: PropTypes.func,
};

DataTable.defaultProps = {
  actions: ["view", "edit", "delete"],
  getRowId: (row) => row._id ?? row.id,
  emptyMessage: "No data available",
  paginationAlign: "center",
  itemsPerPageOptions: [10, 25, 50, 100],
};

export default React.memo(DataTable);
